import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator
} from "@angular/forms";
import { merge } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
  tap
} from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { LogClass, Logger } from "src/app/logger";
import { PersonFacade } from "src/app/services/person.facade";
import { SubSink } from "subsink";
import { PeoplePickerComponent } from "../people-picker/people-picker.component";

// see https://javascript-conference.com/blog/angular-reactive-forms-building-custom-form-controls/
// and https://dev.to/bitovi/understanding-angular-s-control-value-accessor-interface-5e7k

/**
 * People-Picker
 * This is a picker for one or more People
 *
 * usage:
 *  <sp-people-picker [maxPeople]="1"></sp-people-picker>
 *
 * options
 *  maxPeople: number?
 *   if set to 1, validation will fail if more than one person is selected
 *
 * use it in a form to get auto-validation.
 *
 * TODO:
 * - ad-groups / sharepoint-groups untested
 * - pre-filled data untestd
 * - using it without a form untested
 *
 */
@LogClass()
@Component({
    selector: "sp-people-picker",
    templateUrl: "./../people-picker/people-picker.component.html",
    styleUrls: ["./../people-picker/people-picker.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SpPeoplePickerComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: SpPeoplePickerComponent,
            multi: true
        }
    ],
    standalone: false
})
export class SpPeoplePickerComponent
  extends PeoplePickerComponent
  implements OnDestroy, OnInit, ControlValueAccessor, Validator
{
  private subscriptions = new SubSink();
  private logger: Logger;

  constructor(private personFacade: PersonFacade, private i18n: I18nService) {
    super();
    this.setNoDataText("picker_people_typeToSearch");
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    const searchTextDistinct$ = this.filterChange.pipe(
      tap((text) => this.logger.debug(text)),
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => this.logger.debug("After distinct"))
    );

    const noSearchText$ = searchTextDistinct$.pipe(
      filter((text) => !text), // No search Text
      tap(() => {
        this.logger.debug("No search text");
        this.setNoDataText("picker_people_typeToSearch");
      }),
      map(() => [])
    );

    const searchText$ = searchTextDistinct$.pipe(
      filter((text) => !!text), // With search Text
      tap(() => {
        this.logger.debug("Loading");
        this.setNoDataText("picker_people_searching");
        this.loading = true;
      }),
      switchMap((text) =>
        this.personFacade
          .search(text)
          .pipe(
            tap((p) =>
              this.logger.info(p.length + " matches for search: " + text)
            )
          )
      ), // tap in switchMap -> "text" available
      tap((p) => {
        if (p.length === 0) {
          this.setNoDataText("picker_people_noData");
        }
        this.loading = false;
        this.logger.debug("Loading finished");
      })
    );

    this.subscriptions.sink = merge(noSearchText$, searchText$).subscribe(
      (d) => (this.data = d)
    );
  }

  private setNoDataText(key: string) {
    this.noDataText = "...";
    this.i18n
      .get(key)
      .pipe(first())
      .subscribe((v) => (this.noDataText = v));
  }
}
